/* When basic training opens by itself.

   The rule is the server's: `User.basic_training_seen_at` is null until the tutorial has been
   opened for the account once, and the client walks a signed-in player into `/tutorial` while it
   is. On the row rather than in this browser so a player who registers on one device and next
   opens the game on another is asked once, not twice — and never again after that, because the
   view marks it the moment it opens (`markBasicTrainingSeen`), whether or not the lesson is
   finished. The manual offers it back to anyone who wants it.

   Pure, and kept out of `routes.js`, so the decision the sign-up flow rests on can be tested
   without rendering the router. */

import { localizedPath, stripLanguage } from "./languagePaths"

/** Where the automatic start sends a player: the tutorial at *their language's* address.

    A bare `/tutorial` for a Spanish player is not wrong, it is one navigation short:
    `LanguageInPath` moves it to `/es/tutorial` a beat later, the route remounts under
    `AnimatePresence`, and the two mounts of `views/Tutorial` then fight over one local server —
    the outgoing one's teardown ran after the incoming one had started, and the lesson booted for
    ever on "Setting up the field…" (found registering in Spanish, 2026-09-24). Landing on the
    final address is what keeps it to one mount. */
export const basicTrainingPath = (language) => localizedPath("/tutorial", language)

/** Whether a `/users/me/` payload says the tutorial has yet to be opened for this account.

    Only an explicit `null` counts. A server older than the column sends nothing at all, and
    reading "absent" as "pending" would walk every player on that server into the lesson. */
export const basicTrainingPendingFrom = (data) => data?.basic_training_seen_at === null

/* Routes the automatic start leaves alone, besides `/tutorial` itself:

   - `/lobby/…` — an invite link. Somebody a friend sent here has a game waiting with a person in
     it, which is a better first lesson than a scripted one; the flag stays pending and the
     tutorial opens the next time they land somewhere it may.
   - `/reset-password/…` — a form mid-flight. */
const LEFT_ALONE = ["/tutorial", "/lobby/", "/reset-password/"]

export const shouldStartBasicTraining = ({ isAuthenticated, pending, pathname }) => {
  if (!isAuthenticated || !pending) return false
  // `/es/tutorial` is the tutorial: the language lives in front of a public path, never inside it.
  const bare = stripLanguage(pathname)
  return !LEFT_ALONE.some((prefix) => bare === prefix || bare.startsWith(prefix))
}
